/**
 * AgingCartera — Vista global de antigüedad de cartera por cobrar.
 *
 * Lee GET /turismo/finanzas/aging y muestra:
 *   - Totales en 4 buckets (0-30 / 30-60 / 60-90 / >90)
 *   - Barra apilada de % por bucket
 *   - Tabla cruzada agencia × bucket (ordenada por mayor riesgo: las que más
 *     tienen en >90 días aparecen primero).
 *   - Click en agencia → navega a su Estado de cuenta.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, TextField, Button, Chip, CircularProgress, IconButton,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Tooltip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

const API_BASE = process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api';

function getEmpresaId() {
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    return localStorage.getItem('empresa_activa') || u.company_id || u.empresa_id || '1';
  } catch { return '1'; }
}
function getAuthHeaders() {
  const token = localStorage.getItem('token');
  return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}
function fmtMoney(n) {
  return '$' + new Intl.NumberFormat('es-CO', { minimumFractionDigits: 0 }).format(Number(n || 0));
}
function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

// Definición de buckets — fuente única de verdad. Si se cambian umbrales, se
// cambian acá y en el backend (finanzas_global.py).
const BUCKETS = [
  { key: 'rango_0_30',   label: '0 – 30 días',     short: '0-30',  color: '#10b981', bg: '#dcfce7', sev: 0 },
  { key: 'rango_30_60',  label: '30 – 60 días',    short: '30-60', color: '#0ea5e9', bg: '#e0f2fe', sev: 1 },
  { key: 'rango_60_90',  label: '60 – 90 días',    short: '60-90', color: '#f59e0b', bg: '#fef3c7', sev: 2 },
  { key: 'rango_90_mas', label: 'Más de 90 días',  short: '>90',   color: '#dc2626', bg: '#fee2e2', sev: 3 },
];


export default function AgingCartera() {
  const navigate = useNavigate();
  const empresaId = getEmpresaId();
  const [fechaCorte, setFechaCorte] = useState(todayISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(
        `${API_BASE}/turismo/finanzas/aging?empresa_id=${empresaId}&fecha_corte=${fechaCorte}`,
        { headers: getAuthHeaders() }
      );
      setData(await r.json());
    } catch { setData(null); }
    finally { setLoading(false); }
  }, [empresaId, fechaCorte]);

  useEffect(() => { cargar(); }, [cargar]);

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#134e4a', mb: 0.5 }}>
          Aging de cartera
        </Typography>
        <Typography sx={{ fontSize: 13, color: '#64748b' }}>
          Antigüedad de las cuentas por cobrar a agencias. Las agencias con más cartera vieja aparecen primero.
        </Typography>
      </Box>

      {/* Controles */}
      <Paper sx={{ p: 1.5, mb: 2, borderRadius: 2, bgcolor: '#fff', border: '1px solid #e2e8f0',
          display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
        <TextField
          size="small" type="date" label="Fecha de corte"
          InputLabelProps={{ shrink: true }}
          value={fechaCorte}
          onChange={(e) => setFechaCorte(e.target.value)}
          sx={{ minWidth: 160 }}
        />
        <IconButton onClick={cargar} sx={{ bgcolor: '#f1f5f9' }}>
          <RefreshIcon />
        </IconButton>
        <Box sx={{ flex: 1 }} />
        {data && (
          <Typography sx={{ fontSize: 11, color: '#64748b' }}>
            {data.totales.num_agencias_con_saldo} agencia(s) con saldo · {data.totales.num_reservas} reserva(s)
          </Typography>
        )}
      </Paper>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress sx={{ color: '#0d9488' }} />
        </Box>
      )}

      {!loading && data && data.totales.cxc_total === 0 && (
        <Paper sx={{ p: 5, textAlign: 'center', borderRadius: 2, bgcolor: '#f0fdf4', border: '1px dashed #16a34a' }}>
          <Typography sx={{ color: '#166534', fontSize: 16, fontWeight: 700, mb: 0.5 }}>
            ✓ Cartera al día
          </Typography>
          <Typography sx={{ color: '#64748b', fontSize: 13 }}>
            Ninguna agencia tiene saldo pendiente al corte {data.fecha_corte}.
          </Typography>
        </Paper>
      )}

      {!loading && data && data.totales.cxc_total > 0 && (
        <>
          <TotalCard totales={data.totales} />
          <BarraApilada totales={data.totales} />
          <TablaAgencias
            agencias={data.agencias}
            onAbrirAgencia={(id) => navigate(`/finanzas/estado-cuenta?agencia=${id}`)}
          />
        </>
      )}
    </Box>
  );
}


function TotalCard({ totales }) {
  return (
    <Paper sx={{ p: 2, mb: 2, borderRadius: 2, border: '1px solid #e2e8f0',
        bgcolor: 'linear-gradient(135deg, #f0fdfa 0%, #fff 100%)' }}>
      <Typography sx={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', fontWeight: 700, letterSpacing: 0.5 }}>
        Total en cartera
      </Typography>
      <Typography sx={{ fontSize: 36, fontWeight: 800, color: '#dc2626', lineHeight: 1 }}>
        {fmtMoney(totales.cxc_total)}
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' }, gap: 1.5, mt: 2 }}>
        {BUCKETS.map(b => {
          const data = totales[b.key] || { monto: 0, num_reservas: 0, num_agencias: 0 };
          const pct = totales.cxc_total > 0 ? (data.monto / totales.cxc_total) * 100 : 0;
          return (
            <Box key={b.key} sx={{
              p: 1.25, borderRadius: 1.5, bgcolor: b.bg, borderLeft: `4px solid ${b.color}`,
            }}>
              <Typography sx={{ fontSize: 9.5, color: '#475569', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {b.label}
              </Typography>
              <Typography sx={{ fontSize: 18, fontWeight: 800, color: b.color, lineHeight: 1.1 }}>
                {fmtMoney(data.monto)}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                <Typography sx={{ fontSize: 10, color: '#64748b' }}>
                  {data.num_reservas} res · {data.num_agencias} ag.
                </Typography>
                <Typography sx={{ fontSize: 10, color: b.color, fontWeight: 700 }}>
                  {pct.toFixed(1)}%
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
}


function BarraApilada({ totales }) {
  const total = totales.cxc_total;
  if (total <= 0) return null;
  return (
    <Paper sx={{ p: 2, mb: 2, borderRadius: 2, border: '1px solid #e2e8f0' }}>
      <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>
        Distribución por antigüedad
      </Typography>
      <Box sx={{ display: 'flex', height: 22, borderRadius: 1, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
        {BUCKETS.map(b => {
          const monto = totales[b.key]?.monto || 0;
          const pct = (monto / total) * 100;
          if (pct === 0) return null;
          return (
            <Tooltip key={b.key} title={`${b.label}: ${fmtMoney(monto)} (${pct.toFixed(1)}%)`}>
              <Box sx={{
                width: `${pct}%`, bgcolor: b.color,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'help',
              }}>
                {pct >= 8 && (
                  <Typography sx={{ fontSize: 10, color: '#fff', fontWeight: 700 }}>
                    {pct.toFixed(0)}%
                  </Typography>
                )}
              </Box>
            </Tooltip>
          );
        })}
      </Box>
      <Box sx={{ display: 'flex', gap: 2, mt: 1, flexWrap: 'wrap' }}>
        {BUCKETS.map(b => (
          <Box key={b.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 10, height: 10, bgcolor: b.color, borderRadius: 0.5 }} />
            <Typography sx={{ fontSize: 11, color: '#475569' }}>
              {b.short}
            </Typography>
          </Box>
        ))}
      </Box>
    </Paper>
  );
}


function TablaAgencias({ agencias, onAbrirAgencia }) {
  return (
    <Paper sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
      <Box sx={{ p: 1.5, bgcolor: '#fff', borderBottom: '1px solid #e2e8f0' }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#134e4a' }}>
          Cartera por agencia ({agencias.length})
        </Typography>
        <Typography sx={{ fontSize: 11, color: '#64748b' }}>
          Ordenadas por mayor riesgo: las que más tienen en cartera vieja aparecen primero.
        </Typography>
      </Box>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#f8fafc' }}>
              <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Agencia</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, fontSize: 11 }}>Reservas</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11 }}>Total</TableCell>
              {BUCKETS.map(b => (
                <TableCell key={b.key} align="right" sx={{ fontWeight: 700, fontSize: 11, color: b.color }}>
                  {b.short}
                </TableCell>
              ))}
              <TableCell align="center" sx={{ fontWeight: 700, fontSize: 11 }}>Máx. días</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, fontSize: 11, width: 50 }}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {agencias.map(a => {
              const criticoMax = Math.max(
                a.rango_90_mas,
                a.rango_60_90,
              );
              const tieneCritico = a.rango_90_mas > 0 || a.rango_60_90 > 0;
              return (
                <TableRow key={a.agencia_id} hover sx={{ cursor: 'pointer' }}
                  onClick={() => onAbrirAgencia(a.agencia_id)}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {tieneCritico && (
                        <Tooltip title="Tiene cartera con más de 60 días">
                          <WarningAmberIcon sx={{ fontSize: 14, color: '#dc2626' }} />
                        </Tooltip>
                      )}
                      <Box>
                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>
                          {a.agencia_nombre}
                        </Typography>
                        {a.ciudad && (
                          <Typography sx={{ fontSize: 10, color: '#64748b' }}>
                            {a.ciudad}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell align="center" sx={{ fontSize: 11 }}>
                    <Chip size="small" label={a.num_reservas}
                      sx={{ height: 18, fontSize: 10, bgcolor: '#eff6ff', color: '#1d4ed8', fontWeight: 700 }} />
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: 12, fontWeight: 800, color: '#dc2626' }}>
                    {fmtMoney(a.cxc_total)}
                  </TableCell>
                  {BUCKETS.map(b => {
                    const monto = a[b.key] || 0;
                    return (
                      <TableCell key={b.key} align="right" sx={{
                        fontSize: 11,
                        fontWeight: monto > 0 ? 700 : 400,
                        color: monto > 0 ? b.color : '#cbd5e1',
                        bgcolor: monto > 0 ? b.bg : 'transparent',
                      }}>
                        {monto > 0 ? fmtMoney(monto) : '—'}
                      </TableCell>
                    );
                  })}
                  <TableCell align="center">
                    <Chip size="small" label={`${a.antiguedad_max_dias}d`}
                      sx={{
                        height: 18, fontSize: 10, fontWeight: 700,
                        bgcolor: a.antiguedad_max_dias > 90 ? '#fee2e2'
                          : a.antiguedad_max_dias > 60 ? '#fef3c7'
                          : a.antiguedad_max_dias > 30 ? '#e0f2fe' : '#dcfce7',
                        color: a.antiguedad_max_dias > 90 ? '#991b1b'
                          : a.antiguedad_max_dias > 60 ? '#92400e'
                          : a.antiguedad_max_dias > 30 ? '#075985' : '#166534',
                      }} />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Abrir estado de cuenta">
                      <IconButton size="small" onClick={(e) => {
                        e.stopPropagation();
                        onAbrirAgencia(a.agencia_id);
                      }}>
                        <OpenInNewIcon sx={{ fontSize: 14, color: '#64748b' }} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}
